/***
 *
 *   SCREENING SUMMARY HELPERS
 *   Pure logic that reduces a verification's PEP/Sanctions and adverse media
 *   screening down to the one-cell Yes/No/N/A summaries used by the dashboard
 *   columns and its CSV export. Kept separate from the view so the source_type
 *   split can be unit tested without rendering the whole Dashboard.
 *
 *   Client mirror of helper/screeningStatus.js (countBySourceType /
 *   derivePepStatus / deriveSanctionsStatus) — the client cannot import from
 *   outside client/src, so the split is restated here, the same way
 *   pdf-report.js (derivePdfAvailability) mirrors the server's PDF gating.
 *
 *   PEP and Sanctions share one stored result (sanctions_results.found_records)
 *   and are split by each record's source_type. SOURCE_TYPE_LABELS in
 *   sanctions-record.js is the full set of known values: SANCTION, PEP,
 *   CRIMINAL, OTHER. CRIMINAL/OTHER (and any unrecognised value) fall to the
 *   "other" bucket rather than being dropped, so a hit is never invisible.
 *
 **********/

// Cell values. Kept as Yes/No/N/A to match the sibling Adverse Media column and
// the filterSelectOptions on all three dashboard columns.
export const SCREENING_YES = "Yes";
export const SCREENING_NO = "No";
export const SCREENING_NA = "N/A";

// The module's full value domain, in the order the dashboard filter dropdown
// lists them. Exported so a column's filterSelectOptions cannot drift from the
// values its accessorFn actually returns.
export const SCREENING_FILTER_OPTIONS = [
  SCREENING_YES,
  SCREENING_NO,
  SCREENING_NA,
];

// source_type values inside sanctions_results.found_records (Dilisense).
const SOURCE_TYPE_PEP = "PEP";
const SOURCE_TYPE_SANCTION = "SANCTION";

// Tranche 2 verifications are typed "kyc+tranche2_v1", with no sanctions or
// adverse_media token, yet the Veriff decision webhooks run both PEP/Sanctions
// and adverse media for every one of them on a positive decision (the
// isTranche2TypeVerification gates in veriffController.js). nav-bar-items.js
// applies the same rule to show the detail view's screening tabs.
const TRANCHE2_TYPE_TOKEN = "tranche2_v1";

/**
 * Whether a column should read N/A rather than Yes/No.
 *
 * Other types: N/A only when the type has no `token` ("sanctions" or
 * "adverse_media"); a screened type whose result has not returned yet reads No.
 *
 * Tranche 2: the type implies both checks, but they only run once the KYC
 * check is approved, so the column stays N/A until the check has completed
 * (`hasReturned`) — never a No for a screen that never happened. This takes
 * precedence over any screening token a Tranche 2 type also carries.
 *
 * `type` may be null, hence the optional chaining.
 */
function isNotApplicable(verification, token, hasReturned) {
  const type = verification?.type;
  if (type?.includes(TRANCHE2_TYPE_TOKEN)) return !hasReturned;
  return !type?.includes(token);
}

// Normalised the same way getRecordBadges normalises it in sanctions-record.js,
// so the columns and the per-record badges always agree on a record's type.
function normalizeSourceType(record) {
  return String(record?.source_type || "")
    .trim()
    .toUpperCase();
}

/**
 * Count found_records whose source_type satisfies `matches`. Returns 0 when
 * screening has not returned yet (found_records absent or not an array) — for
 * a non-Tranche 2 type the dashboard shows that as "No".
 */
function countBySourceType(verification, matches) {
  const records = verification?.sanctions_results?.found_records;
  if (!Array.isArray(records)) return 0;
  return records.filter((r) => matches(normalizeSourceType(r))).length;
}

/**
 * Yes/No/N/A for one screening bucket. N/A when the verification type has no
 * sanctions component (nothing was ever screened), or for Tranche 2 until
 * PEP/Sanctions screening has returned. A found_records array is the "did it
 * run" signal, as in derivePepStatus in helper/screeningStatus.js.
 */
function deriveResult(verification, matches) {
  const returned = Array.isArray(
    verification?.sanctions_results?.found_records
  );
  if (isNotApplicable(verification, "sanctions", returned)) return SCREENING_NA;
  return countBySourceType(verification, matches) > 0
    ? SCREENING_YES
    : SCREENING_NO;
}

/**
 * PEP-only summary (sanction / criminal / other records excluded).
 * @param {object} verification - a verification row with type + sanctions_results
 * @returns {string} SCREENING_YES | SCREENING_NO | SCREENING_NA
 */
export function derivePepResult(verification) {
  return deriveResult(verification, (t) => t === SOURCE_TYPE_PEP);
}

/**
 * Sanctions-only summary (PEP / criminal / other records excluded).
 */
export function deriveSanctionsResult(verification) {
  return deriveResult(verification, (t) => t === SOURCE_TYPE_SANCTION);
}

/**
 * Everything that is neither PEP nor sanction — CRIMINAL, OTHER, and any
 * unrecognised or missing source_type. The catch-all exists so that splitting
 * the old combined column can never hide a hit it used to surface.
 */
export function deriveOtherScreeningResult(verification) {
  return deriveResult(
    verification,
    (t) => t !== SOURCE_TYPE_PEP && t !== SOURCE_TYPE_SANCTION
  );
}

/**
 * Adverse media summary. adverse_media_results holds the raw iDenfy AML
 * response rather than sanctions_results' found_records, so it cannot go
 * through deriveResult above: hits are AML[0].data.length, and an explicit
 * serviceSuspected === false ("we screened, nothing found") zeroes them out
 * whatever data is still on the node. Mirrors getAdverseMediaMatchCount in
 * veriffController.js, so the dashboard cell and the server agree on a row.
 * For Tranche 2 an AML[0] node is the "did it run" signal, as in
 * deriveAdverseMediaStatus in helper/screeningStatus.js.
 *
 * @param {object} verification - a verification row with type + adverse_media_results
 * @returns {string} SCREENING_YES | SCREENING_NO | SCREENING_NA
 */
export function deriveAdverseMediaResult(verification) {
  const aml = verification?.adverse_media_results?.AML?.[0];
  if (isNotApplicable(verification, "adverse_media", Boolean(aml)))
    return SCREENING_NA;

  const hits =
    aml && aml.status?.serviceSuspected !== false && Array.isArray(aml.data)
      ? aml.data.length
      : 0;

  return hits > 0 ? SCREENING_YES : SCREENING_NO;
}
